import ApiCall from './ApiCall';
import EventHandling from './EventHandling';
import ModuleListener from './ModuleListener';
import postMessage from './PostMessage';
import DataInjector from './DataInjector';
import Analytics from './Analytics';
import AjaxInterceptor from './AjaxInterceptor';
import { normalizeAllowlist, safeParseMessage } from './MessageGuard';
import { setModuleApiBase } from './config';

// --- check if instanceId is in ab object
function checkAbObject (abObj) {
  return new Promise((resolve, reject) => {
    if (abObj.instanceId) {
      return resolve(abObj);
    }
    ApiCall.getModuleInstance(
      abObj.accessToken,
      abObj.accountId,
      abObj.moduleId,
      abObj.env,
    ).then(instance => {
      if (!instance || !instance.id) {
        ApiCall.postModuleInstance(
          abObj.accessToken,
          abObj.accountId,
          abObj.moduleId,
          abObj.env,
        ).then(instance => {
          abObj.instanceId = instance.id;
          return resolve(abObj);
        });
      } else {
        abObj.instanceId = instance.id;
        return resolve(abObj);
      }
    });
  });
}

function addIframeEventListener (
  iframe,
  ab,
  readyCallback,
  waitCallback,
  heightUpdatedCallback,
  eventCallback,
  trackCallback,
  allowedOrigins,
) {
  const allowlist = normalizeAllowlist(allowedOrigins);
  window.addEventListener(
    'message',
    event => {
      if (!iframe || iframe.contentWindow !== event.source) {
        return;
      }

      const dataObj = safeParseMessage(event, allowlist);
      if (!dataObj) {
        return;
      }

      switch (dataObj.action) {
        case 'ready':
          readyCallback && readyCallback();
          break;
        case 'wait':
          waitCallback && waitCallback();
          break;
        case 'setHeight':
          heightUpdatedCallback && heightUpdatedCallback(dataObj.data);
          break;
        case 'event':
          eventCallback && eventCallback(dataObj.data.event, dataObj.data.data);
          break;
        case 'track':
          const data = dataObj.data.data;
          data.moduleId = (data && data.moduleId) || ab.moduleId;
          data.accountId = (data && data.accountId) || ab.accountId;
          data.instanceId = (data && data.instanceId) || ab.instanceId;
          data.testId = (data && data.testId) || ab.testId;

          Analytics.sendHeapEvent(dataObj.data.event, data);
          trackCallback && trackCallback(dataObj.data.event, data);
          break;
        default:
          break;
      }
    },
    false,
  );
}

// ---load code into iframe

function loadParamsIntoIframe (iframe, ab) {
  if (ab.dev) {
    return Analytics.trackingInject(iframe, ab.targetOrigin).then(() => {
      return DataInjector.devDataInject(iframe, ab);
    });
  }
  return checkAbObject(ab).then(ab => {
    let instanceData = false;
    return ApiCall.getInstanceConfig(
      ab.accessToken,
      ab.accountId,
      ab.moduleId,
      ab.instanceId,
      ab.env,
    )
      .then(data => {
        instanceData = data;
        return Analytics.trackingInject(iframe, ab.targetOrigin);
      })
      .then(() => {
        return DataInjector.prodDataInject(
          iframe,
          ab,
          instanceData,
          instanceData.id,
        );
      });
  });
}

function loadModuleIntoIframe (frame, { accessToken, accountId, testId, instanceScope, initialData, env, ABTastyApiUrl, profile, currentAccount, userRole, allowedOrigins, targetOrigin }) {
  return new Promise(((resolve) => {
    const _ab = {
      env,
      currentAccount,
      ABTastyApiUrl,
      accessToken,
      accountId,
      testId,
      profile,
      userRole,
      instanceScope,
      module: initialData || {},
      allowedOrigins: allowedOrigins || '*',
      targetOrigin: targetOrigin || '*',
      ready: null,
      wait: null,
      storage: {},
    };

    function ready () {
      window.parent.postMessage('{"action":"ready"}', (window._ab && window._ab.targetOrigin) || '*');
    }

    function wait () {
      window.parent.postMessage('{"action":"wait"}', (window._ab && window._ab.targetOrigin) || '*');
    }

    // add height listener
    function heightListener () {
      let height = 0;

      function post (heightParam) {
        window.parent.postMessage(
          `{"action":"setHeight","data":${heightParam}}`,
          (window._ab && window._ab.targetOrigin) || '*',
        );
      }

      function getHeight () {
        const { body, documentElement } = document;

        const computedHeight = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          documentElement.clientHeight,
          documentElement.scrollHeight,
          documentElement.offsetHeight,
        );
        return computedHeight;
      }

      setInterval(() => {
        const currentHeight = getHeight();
        if (currentHeight !== height) {
          height = currentHeight;
          post(height);
        }
      }, 500);
    }

    postMessage(
      frame,
      'evalCode',
      `
          window._ab=${JSON.stringify(_ab)};
          window._ab.ready=${ready};
          window._ab.wait=${wait};
          window._ab.events=(${EventHandling})();
          (${heightListener})();
      `,
      targetOrigin,
    );
    resolve();
  }));
}

function loadListenerIntoIframe (iframe, allowedOrigins) {
  return new Promise((resolve, reject) => {
    const allowlistValue = allowedOrigins || '*';
    const allowlistJson = JSON.stringify(allowlistValue)
      .replace(/</g, '\\u003c')
      .replace(/>/g, '\\u003e')
      .replace(/\u2028/g, '\\u2028')
      .replace(/\u2029/g, '\\u2029');
    const iframeHtml = `<html><head></head><body><script>window.__abAllowedOrigins=${allowlistJson};(${ModuleListener.toString()})()</script></body></html>`;
    iframe.srcdoc = iframeHtml;
    // for edge, use src attribute
    iframe.src = `data:text/html;charset=utf-8,${encodeURIComponent(
      `<script>window.__abAllowedOrigins=${allowlistJson};(${ModuleListener.toString()})()</script>`,
    )}`;
    iframe.onload = () => {
      resolve();
    };
  });
}

function loadScriptIntoModule (frame, ab, script) {
  if (script) {
    postMessage(frame, 'evalCode', script, ab.targetOrigin || '*');
    return new Promise((resolve, reject) => resolve());
  }

  return ApiCall.getInstanceSource(
    ab.accessToken,
    ab.accountId,
    ab.moduleId,
    ab.instanceId,
    ab.version,
    ab.env,
  ).then(script => {
    return postMessage(frame, 'evalCode', script, ab.targetOrigin || '*');
  });
}

function resolveTargetOrigin(targetOrigin) {
  return typeof targetOrigin === 'string' && targetOrigin ? targetOrigin : '*';
}

function serializeAllowedOrigins(allowedOrigins) {
  const normalizedAllowlist = normalizeAllowlist(allowedOrigins);
  if (normalizedAllowlist === '*') {
    return '*';
  }

  if (normalizedAllowlist instanceof Set) {
    return Array.from(normalizedAllowlist);
  }

  return '*';
}

function inject ({
  iframeElement,
  ab: {
    env,
    ABTastyApiUrl,
    profile,
    currentAccount,
    userRole,
    accessToken,
    accountId,
    testId,
    moduleId,
    instanceId,
    instanceScope,
    dev,
    initialData,
    version,
    allowedOrigins: abAllowedOrigins,
    targetOrigin: abTargetOrigin,
  },
  allowedOrigins,
  targetOrigin,
  script,
  injectedCallback,
  readyCallback,
  waitCallback,
  heightUpdatedCallback,
  eventCallback,
  trackCallback,
}) {
  const resolvedTargetOrigin = resolveTargetOrigin(targetOrigin || abTargetOrigin);
  const resolvedAllowedOrigins = serializeAllowedOrigins(allowedOrigins || abAllowedOrigins);
  const ab = {
    env,
    ABTastyApiUrl,
    profile,
    currentAccount,
    userRole,
    accessToken,
    accountId,
    testId,
    moduleId,
    instanceId,
    instanceScope,
    dev,
    initialData,
    version,
    allowedOrigins: resolvedAllowedOrigins,
    targetOrigin: resolvedTargetOrigin,
  };
  if (iframeElement) {
    iframeElement.__abTargetOrigin = resolvedTargetOrigin;
  }
  addIframeEventListener(
    iframeElement,
    ab,
    readyCallback,
    waitCallback,
    heightUpdatedCallback,
    eventCallback,
    trackCallback,
    resolvedAllowedOrigins,
  );
  return loadListenerIntoIframe(iframeElement, resolvedAllowedOrigins)
    .then(() => {
      return loadModuleIntoIframe(iframeElement, ab);
    })
    .then(() => {
      return loadParamsIntoIframe(iframeElement, ab);
    })
    .then(() => {
      return AjaxInterceptor.loadAjaxInterceptor(iframeElement, ab.targetOrigin);
    })
    .then(() => {
      return loadScriptIntoModule(iframeElement, ab, script);
    })
    .then(() => {
      const { accountId, moduleId, instanceId } = ab;
      Analytics.sendHeapEvent('moduleLoaded', {
        accountId,
        moduleId,
        instanceId,
      });
      injectedCallback && injectedCallback();
    })
    .catch(err => {
      console.log('caught : ', err);
      window.parent.postMessage(
        JSON.stringify({
          action: 'httpError',
          data: {
            status: '401',
          },
        }),
        resolvedTargetOrigin,
      );
    });
}

function updateAccessToken (frame, newAccessToken, targetOrigin) {
  const resolvedTargetOrigin = (frame && frame.__abTargetOrigin) || targetOrigin || '*';
  postMessage(frame, 'event', {
    event: 'updateToken',
    data: {
      accessToken: newAccessToken,
    },
  }, resolvedTargetOrigin);
}

export { updateAccessToken, setModuleApiBase };

export default {
  inject,
};
